import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './wild-tales.css';

const STAGE_NUMBER = 8;
const GAME_DISPLAY_TITLE = 'WILD TALES';
const PARTICIPATION_POINTS = 1;
const NAME_MAX = 50;
const DETAILS_MAX = 100;
const DEFAULT_PLAY_SECONDS = 300;
const STAGE5_PLAY_DEADLINE_PREFIX = 'stage5_play_deadline';

const AVATAR_COLORS = ['#5b6cf0', '#7c5bf0', '#4f8ef7', '#6b5ce7', '#8b6cf0', '#3b82f6'];

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function parseStoryEntries(stage5) {
  const raw = stage5?.story_entries ?? stage5?.examples ?? stage5?.questions;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (typeof item === 'string') {
        const text = item.trim();
        if (!text) return null;
        return {
          id: `entry-${index}`,
          name: `Player ${index + 1}`,
          animal: '',
          text,
          avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
        };
      }
      const text = String(item?.text ?? item?.sentence ?? item?.line ?? '').trim();
      if (!text) return null;
      return {
        id: `entry-${index}`,
        name: String(item?.name ?? item?.player ?? 'Teammate').trim(),
        animal: String(item?.animal ?? '').trim(),
        text,
        avatarColor: item?.color ?? AVATAR_COLORS[index % AVATAR_COLORS.length],
      };
    })
    .filter(Boolean);
}

function avatarInitials(name) {
  const parts = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function stage5PlayDeadlineKey(userId, sessionId) {
  return `${STAGE5_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage5PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage5PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage5PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function ClockIcon() {
  return (
    <svg className="s5-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

export default function Stage5({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(5);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage5 = themeData?.stages_data?.stage_8;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage5?.timer, DEFAULT_PLAY_SECONDS),
    [stage5?.timer]
  );

  const logoUrl = s3PublicUrl(stage5?.logo ?? stage5?.badge);
  const rulesList = getStageRulesList(stage5);
  const subtitle = String(stage5?.title ?? stage5?.name ?? 'Once upon a jungle…');
  const storyStarter = useMemo(
    () => formatStageDescription(stage5?.description),
    [stage5?.description]
  );
  const storyEntries = useMemo(() => parseStoryEntries(stage5), [stage5]);

  const isConfigured = !!stage5 && String(stage5?.title ?? '').trim().length > 0;

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);
  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's5'), [visuals]);

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const nameInputRef = useRef(null);
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);

  const [entryName, setEntryName] = useState('');
  const [entryDetails, setEntryDetails] = useState('');
  const [localEntries, setLocalEntries] = useState([]);
  const [userAddedEntries, setUserAddedEntries] = useState([]);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted: isSubmitting,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted: isSubmitting,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage5,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);
  const inputsLocked = isSubmitting || timerExpired;

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    const mapped = storyEntries.map((entry, index) => ({
      id: entry.id || `saved-${index}`,
      name: String(entry.name ?? '').trim() || `Player ${index + 1}`,
      details: String(entry.text ?? '').trim(),
    }));
    setLocalEntries(mapped);
  }, [storyEntries]);

  useEffect(() => {
    if (!isConfigured || isSubmitting || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    isSubmitting,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmitting) return;
    if (!auto && !userAddedEntries.length) {
      triggerShake();
      setMessage('Please add at least one new entry');
      nameInputRef.current?.focus();
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const trimmedAnswer = JSON.stringify(localEntries);

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setIsSubmitting(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setIsSubmitting(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
        timeSeconds: stageTimeSeconds,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });
    } catch (err) {
      setIsSubmitting(false);
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  return (
    <div className="stage5-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 8 is not configured: set stage_8 title and description in the theme.
          </p>
        }
      >
        <>
        <div className="stage-page-main stage5-main">
          <header className="s5-top-bar">
            <div
              className={`s5-top-timer${timerExpired ? ' s5-top-timer--expired' : ''}`}
              aria-label="Stage timer"
              aria-live="polite"
            >
              <ClockIcon />
              <span className="s5-timer-value">{countdownDisplay}</span>
            </div>
            {rulesList.length > 0 ? (
              <button
                type="button"
                className="stage5-rules-fab"
                onClick={openRules}
                aria-label="How to play"
                title="How to play"
              >
                ?
              </button>
            ) : null}
          </header>

          <div className="s5-heading">
            <div className="s5-game-title">
              {logoUrl && !logoFailed ? (
                <img
                  src={logoUrl}
                  alt={GAME_DISPLAY_TITLE}
                  className="s5-game-logo"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <h1 className="s5-game-title-text">{GAME_DISPLAY_TITLE}</h1>
              )}
            </div>
            {subtitle ? <p className="s5-game-subtitle">{subtitle}</p> : null}
          </div>

          <div className="s5-game-row">
            {storyStarter ? (
              <section className="s5-description-panel" aria-label="Story starter description">
                <div className="s5-description-panel-header">Description</div>
                <p className="s5-description-panel-text">{storyStarter}</p>
                {storyEntries.length > 0 ? (
                  <section className="s5-story-feed" aria-label="Story so far">
                    {storyEntries.map((entry) => (
                      <article key={entry.id} className="s5-story-entry">
                        <div
                          className="s5-story-avatar"
                          style={{ backgroundColor: entry.avatarColor }}
                          aria-hidden
                        >
                          {avatarInitials(entry.name)}
                        </div>
                        <div className="s5-story-entry-body">
                          <p className="s5-story-entry-meta">
                            <strong>{entry.name.toUpperCase()}</strong>
                            {entry.animal ? (
                              <span className="s5-story-entry-animal">
                                {' '}
                                ({entry.animal})
                              </span>
                            ) : null}
                          </p>
                          <p className="s5-story-entry-text">{entry.text}</p>
                        </div>
                      </article>
                    ))}
                  </section>
                ) : null}
              </section>
            ) : null}

            <div className="s5-play-area">
              <div className="s5-story-card">
                <form className="s5-turn-form" onSubmit={handleSubmit}>
                  {userAddedEntries.length > 0 ? (
                    <section className="s5-center-feed" aria-label="Added entries preview">
                      {userAddedEntries.map((entry) => (
                        <article key={entry.id} className="s5-center-entry">
                          <div
                            className="s5-story-avatar"
                            style={{ backgroundColor: entry.avatarColor }}
                            aria-hidden
                          >
                            {avatarInitials(entry.name)}
                          </div>
                          <div className="s5-story-entry-body">
                            <p className="s5-story-entry-meta">
                              <strong>{entry.name.toUpperCase()}</strong>
                            </p>
                            <p className="s5-story-entry-text">{entry.details}</p>
                          </div>
                        </article>
                      ))}
                    </section>
                  ) : null}

                  <div className="s5-two-field-grid">
                    <div className="s5-form-group">
                      <label className="s5-section-label" htmlFor="stage5-entry-name">
                        Name
                      </label>
                      <input
                        ref={nameInputRef}
                        id="stage5-entry-name"
                        type="text"
                        className="s5-text-input"
                        value={entryName}
                        onChange={(ev) => {
                          setEntryName(ev.target.value.slice(0, NAME_MAX));
                          if (message) setMessage('');
                        }}
                        maxLength={NAME_MAX}
                        placeholder="Enter name..."
                        disabled={inputsLocked}
                      />
                    </div>

                    <div className="s5-form-group">
                      <label className="s5-section-label" htmlFor="stage5-entry-details">
                        Details
                      </label>
                      <textarea
                        id="stage5-entry-details"
                        className="s5-textarea-input"
                        value={entryDetails}
                        onChange={(ev) => {
                          setEntryDetails(ev.target.value.slice(0, DETAILS_MAX));
                          if (message) setMessage('');
                        }}
                        maxLength={DETAILS_MAX}
                        placeholder="Enter details..."
                        disabled={inputsLocked}
                        aria-label="Details"
                      />
                    </div>
                  </div>

                  <div className="s5-action-row">
                    <div
                      className={`s5-turn-input-wrap${inputShake ? ' s5-turn-input-wrap--shake' : ''}`}
                    >
                      <button
                        type="button"
                        className="s5-turn-submit-icon"
                        disabled={
                          inputsLocked ||
                          String(entryName ?? '').trim() === '' ||
                          String(entryDetails ?? '').trim() === ''
                        }
                        aria-label="Add entry"
                        onClick={() => {
                          const nextName = String(entryName ?? '').trim();
                          const nextDetails = String(entryDetails ?? '').trim();
                          if (!nextName || !nextDetails) {
                            triggerShake();
                            setMessage('Please enter both name and details');
                            return;
                          }
                          setLocalEntries((prev) => [
                            ...prev,
                            {
                              id: `new-${Date.now()}-${prev.length}`,
                              name: nextName,
                              details: nextDetails,
                            },
                          ]);
                          setUserAddedEntries((prev) => [
                            ...prev,
                            {
                              id: `preview-${Date.now()}-${prev.length}`,
                              name: nextName,
                              details: nextDetails,
                              avatarColor: AVATAR_COLORS[prev.length % AVATAR_COLORS.length],
                            },
                          ]);
                          setEntryName('');
                          setEntryDetails('');
                          setMessage('');
                        }}
                      >
                        <i className="fa-solid fa-plus" aria-hidden />
                      </button>
                      <span className="s5-add-entry-label">Add</span>
                    </div>

                    <button
                      type="submit"
                      className="s5-submit-btn"
                      disabled={inputsLocked || userAddedEntries.length === 0}
                    >
                      {isSubmitting ? 'Submitting…' : 'Submit'}
                    </button>
                  </div>
                </form>

                {message ? <div className="s5-hint">{message}</div> : null}
              </div>
            </div>

            <div className="stage-page-puzzle-aside stage5-puzzle-aside">
              <StagePuzzleReveal themeData={themeData} />
            </div>
          </div>
        </div>
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage5-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage5-rules-title"
          onClick={closeRules}
        >
          <div className="stage5-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage5-rules-title">How to play</h2>
            <ul className="stage5-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage5-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRevealOverlay ? (
        <div
          className="stage-reveal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Revealing your puzzle progress"
          onClick={handleRevealOverlayClick}
        >
          <div className="stage-reveal-confetti" aria-hidden>
            {revealConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="stage-reveal-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
          <div className="stage-reveal-stage">
            <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
              <StagePuzzleReveal
                themeData={themeData}
                completedDaysOverride={revealCompletedDays}
                animateReveal
              />
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
